import { FC, useCallback, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { Alert, Button, Card, Form, Modal, Space, Typography } from "antd"
import { notifications } from "notifications"
import { useStore } from "stores/utils/use-store"
import { Contract, EntitlementFilter } from "../store/types"
import {
  EntitlementFields,
  EntitlementFormValues,
  describeEntitlement,
  entitlementToForm,
  formToEntitlement,
  isUnrestricted,
} from "./entitlement-fields"

/**
 * Editor for a contract's entitlement allow-list — the rules that decide which
 * recordings it may ever receive.
 *
 * The PATCH replaces the set rather than merging it, so this edits the whole
 * list and always sends every rule back. That is also why there is no per-rule
 * save: a single rule is not an addressable thing on the wire.
 */
export const ContractEntitlements: FC<{ contract: Contract }> = observer(({ contract }) => {
  const { dataStore: { dataDeliveryStore: store } } = useStore()
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)

  const reset = useCallback(() => {
    store.clearEntitlementError()
    form.setFieldsValue({ entitlements: (contract.entitlements ?? []).map(entitlementToForm) })
  }, [form, contract, store])

  // Reseeds whenever the contract object changes, which includes the fresh copy
  // a successful save puts on the store — so what is on screen after a save is
  // what the ledger stored, not what was typed.
  useEffect(() => {
    reset()
  }, [reset])

  const rows: EntitlementFormValues[] = Form.useWatch("entitlements", form) ?? []

  const commit = async(next: EntitlementFilter[]) => {
    setSaving(true)
    const ok = await store.saveEntitlements(contract.id, next)
    setSaving(false)
    if (ok) {
      notifications.success(`Entitlements replaced (${next.length} rule${next.length === 1 ? "" : "s"})`)
    }
  }

  const onSave = async() => {
    let v
    try {
      v = await form.validateFields()
    } catch {
      return
    }
    const next: EntitlementFilter[] = ((v.entitlements ?? []) as EntitlementFormValues[]).map(formToEntitlement)

    // Zero rules is a legitimate wire payload but a contract-killing one, so it
    // takes a deliberate second action rather than a single Save click.
    if (next.length === 0) {
      Modal.confirm({
        title: "Save an allow-list with no rules?",
        okText: "Save empty allow-list",
        okButtonProps: { danger: true },
        content: "Both the reserve query and the planner need a rule to match, so this contract would stop matching any "
          + "recording at all and every plan would come back empty. To admit everything instead, keep one rule with all "
          + "five fields blank.",
        onOk: () => commit(next),
      })

      return
    }

    await commit(next)
  }

  const err = store.entitlementError

  return (
    <Card
      size="small"
      title="Entitlements (allow-list)"
      extra={
        <Space>
          <Button size="small" onClick={reset}>Reset to saved</Button>
          <Button type="primary" size="small" loading={saving} onClick={onSave}>Save entitlements</Button>
        </Space>
      }
    >
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        A recording may be delivered under this contract only if it matches at least one rule below. Within a rule every
        listed axis must match; a blank axis means &quot;any&quot;. Saving <b>replaces</b> the stored set with exactly what is
        here — the ledger does not merge — so edit the whole list, not one rule.
      </Typography.Paragraph>

      {err && (
        <Alert
          type={err.blockedByInFlightWork ? "warning" : "error"}
          showIcon
          closable
          style={{ marginBottom: 8 }}
          onClose={() => store.clearEntitlementError()}
          message={err.blockedByInFlightWork ? "Frozen — this contract has work in flight" : "Entitlement save failed"}
          description={
            <Space direction="vertical" size={2}>
              <span>{err.message}</span>
              {err.blockedByInFlightWork && (
                <span>
                  Entitlements are build-affecting and the builder resolves them live, so the ledger refuses the edit while a
                  batch is RESERVED, BUILDING or FAILED, or a selection is still open. Cancel a failed batch you do not
                  intend to retry, or discard a selection you do not intend to execute, then save again.
                </span>
              )}
            </Space>
          }
        />
      )}

      {rows.length === 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 8 }}
          message="No rules — this contract matches nothing"
          description={"Add a rule with all five fields blank to admit every recording, or narrow it to the games, "
            + "countries, tags, UTM source and start date this customer bought."}
        />
      )}

      <Form form={form} layout="vertical" size="small">
        <Form.List name="entitlements">
          {(fields, { add, remove }) => (
            <Space direction="vertical" style={{ width: "100%" }} size={8}>
              {fields.map(field => {
                const summary = describeEntitlement(formToEntitlement(rows[field.name] ?? {}))

                return (
                  <Card
                    key={field.key}
                    size="small"
                    type="inner"
                    title={
                      <Space size={8}>
                        <span>Rule {field.name + 1}</span>
                        <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>{summary}</Typography.Text>
                      </Space>
                    }
                    extra={<Button size="small" danger type="text" onClick={() => remove(field.name)}>Remove</Button>}
                  >
                    <EntitlementFields prefix={[field.name]} compact />
                    {isUnrestricted(formToEntitlement(rows[field.name] ?? {})) && (
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        Every axis blank — this rule admits every recording in the pool.
                      </Typography.Text>
                    )}
                  </Card>
                )
              })}
              <Button size="small" onClick={() => add({})}>Add rule</Button>
            </Space>
          )}
        </Form.List>
      </Form>
    </Card>
  )
})
